/** "Add to Home Screen" flow: the browser's install prompt where there is one, step-by-step help everywhere else. */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { BRAND } from '@platform/shared';
import { platform, promptInstall, useInstallState } from '../../lib/install';
import { Icon, type IconName } from '../ui';

function Step({ n, icon, children }: { n: number; icon?: IconName; children: ReactNode }) {
  return (
    <li className="flex items-start gap-3.5">
      <span className="tnum grid size-8 shrink-0 place-items-center rounded-full bg-pill text-[15px] font-semibold">{n}</span>
      <span className="flex-1 pt-1 text-[16px] leading-snug">
        {children}
        {icon && (
          <span className="ml-1.5 inline-grid size-7 place-items-center rounded-lg bg-pill align-middle text-accent-fg">
            <Icon name={icon} size={17} />
          </span>
        )}
      </span>
    </li>
  );
}

export function InstallHelp({ onClose }: { onClose: () => void }) {
  const os = platform();
  const done = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    done.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-scrim sm:items-center" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="install-title"
        className="sheet-in w-full max-w-[480px] rounded-t-[28px] bg-bg px-6 pt-6 pb-[max(24px,env(safe-area-inset-bottom))] text-fg shadow-pop sm:rounded-[28px]"
      >
        <div className="flex items-center gap-3">
          <img src="/icons/apple-touch-icon.png" alt="" width={48} height={48} className="size-12 shrink-0 rounded-[14px]" />
          <div className="min-w-0">
            <h2 id="install-title" className="m-0 text-[19px] font-semibold">
              Add {BRAND.shortName} to your Home Screen
            </h2>
            <p className="m-0 text-[14px] text-muted">Open it like an app, full screen, in one tap.</p>
          </div>
        </div>

        {os === 'ios' && (
          <>
            <ol className="m-0 mt-6 flex list-none flex-col gap-4 p-0">
              <Step n={1} icon="share">
                Tap the <b>Share</b> button in the browser toolbar
              </Step>
              <Step n={2} icon="addSquare">
                Scroll down and tap <b>Add to Home Screen</b>
              </Step>
              <Step n={3}>
                Tap <b>Add</b> in the top corner
              </Step>
            </ol>
            <p className="m-0 mt-5 text-[13px] text-muted">Opened this link inside another app? Open it in Safari first.</p>
          </>
        )}
        {os === 'android' && (
          <ol className="m-0 mt-6 flex list-none flex-col gap-4 p-0">
            <Step n={1} icon="dots">
              Tap the <b>menu</b> button in your browser
            </Step>
            <Step n={2}>
              Tap <b>Install app</b> or <b>Add to Home screen</b>
            </Step>
            <Step n={3}>
              Tap <b>Install</b> to confirm
            </Step>
          </ol>
        )}
        {os === 'other' && (
          <p className="m-0 mt-6 text-[16px] leading-snug">
            Open this page on your phone and tap <b>Add to Home Screen</b> there. On a computer, Chrome and Edge show an <b>Install</b> icon at the right of the
            address bar.
          </p>
        )}

        <button ref={done} type="button" onClick={onClose} className="mt-6 flex h-[52px] w-full items-center justify-center rounded-full bg-fg text-[17px] font-semibold text-bg">
          Got it
        </button>
      </div>
    </div>
  );
}

/** Button state and handler for "Add to Home Screen". Render `sheet` next to the button. */
export function useAddToHomeScreen() {
  const { installed } = useInstallState();
  const [help, setHelp] = useState(false);
  const close = useCallback(() => setHelp(false), []);
  const start = async () => {
    if ((await promptInstall()) === 'unavailable') setHelp(true);
  };
  return { installed, start, sheet: help ? <InstallHelp onClose={close} /> : null };
}
